/**
 * Operate-UI client for the prompt-discovery + LLM-suggest endpoints
 * powering Feature #2's "Re-run with filters" modal. Uses the shared
 * axios `api` instance so bearer-token resolution + 401 redirects
 * happen via the same interceptor chain as every other call.
 */
import { AxiosError } from "axios";
import { api } from "./api";

export interface ReportPromptDefinition {
  name:          string;
  label:         string;
  /** Wire enum: xsdString / xsdInt / xsdDate / xsdDateTime / memberUniqueName / … */
  dataType:      string;
  /** Wire enum: editBox / selectValue / selectDate / selectInterval / … */
  promptType:    string;
  multiValued:   boolean;
  required:      boolean;
  defaultValues: string[];
  currentValues: string[];
}

export interface PromptSuggestion {
  values:    Record<string, string | string[]>;
  unmapped:  string[];
  rationale: string;
}

export interface PromptsApiError {
  /** Short code: "cognos.sdk.disabled", "llm.gateway.disabled", "cognos.fault", "http.status". */
  code:    string;
  /** Human-readable detail from the backend. */
  detail:  string;
  /** HTTP status, useful for distinguishing 503 (service-down) from 502 (cognos-fault). */
  status:  number;
}

/**
 * Discover a Cognos report's prompt parameter list. Returns an empty
 * array when the report has no prompts; throws {@link PromptsApiError}
 * when the SDK is disabled OR Cognos itself faulted.
 */
export async function fetchReportPrompts(
  searchPath: string,
): Promise<ReportPromptDefinition[]> {
  try {
    const { data } = await api.get<{ prompts?: ReportPromptDefinition[] }>(
      "/cognos/report/prompts",
      { params: { path: searchPath } },
    );
    return Array.isArray(data?.prompts) ? data.prompts : [];
  } catch (e) {
    throw asPromptsApiError(e);
  }
}

/**
 * Map a free-text hint onto a set of prompt definitions via the
 * operate-api LLM endpoint. The backend drops hallucinated parameter
 * names, so callers can apply the result directly without re-validating.
 */
export async function suggestPromptValues(
  searchPath: string,
  hint: string,
  prompts: ReportPromptDefinition[],
): Promise<PromptSuggestion> {
  // Strip currentValues client-side — the LLM doesn't need it and
  // it'd inflate the prompt context cost-wise. Matches the
  // cognos-plugin's behaviour.
  const body = {
    searchPath,
    hint,
    prompts: prompts.map((p) => ({
      name:          p.name,
      label:         p.label,
      dataType:      p.dataType,
      promptType:    p.promptType,
      multiValued:   p.multiValued,
      required:      p.required,
      defaultValues: p.defaultValues,
    })),
  };
  try {
    const { data } = await api.post<{
      values?:    Record<string, unknown>;
      unmapped?:  unknown[];
      rationale?: string;
    }>("/cognos/report/prompts/suggest", body);
    return {
      values:    (data?.values && typeof data.values === "object")
        ? (data.values as Record<string, string | string[]>)
        : {},
      unmapped:  Array.isArray(data?.unmapped)
        ? data.unmapped.filter((v): v is string => typeof v === "string")
        : [],
      rationale: typeof data?.rationale === "string" ? data.rationale : "",
    };
  } catch (e) {
    throw asPromptsApiError(e);
  }
}

function asPromptsApiError(e: unknown): PromptsApiError {
  if (e instanceof AxiosError) {
    const body = e.response?.data as { error?: string; detail?: string } | undefined;
    return {
      code:   typeof body?.error  === "string" ? body.error  : "http.status",
      detail: typeof body?.detail === "string" ? body.detail : (e.message ?? `HTTP ${e.response?.status}`),
      status: e.response?.status ?? 0,
    };
  }
  return {
    code:   "transport.failure",
    detail: e instanceof Error ? e.message : "unknown error",
    status: 0,
  };
}
